import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, DatePicker, Select, Button, Space, Divider, Spin, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty } from 'underscore';

import { AppDispatch, CombinedStates } from '../../../core/types';
import { getIndicators, compareSchools, compareSchoolsTimeSeries, exportSchoolsComparison, exportSchoolsComparisonPdf, getSchoolIndicatorCategories } from '../../../core/indicator/indicatorApi';
import SchoolComparisonResults from './SchoolComparisonResults';
import SchoolSelection from '../SchoolSelection';
import TimeSeriesComparisonResults from './TimeSeriesComparisonResults';
import './style.scss';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const getCurrentAcademicYear = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    const schoolYearStart = now.month() < 8 ? now.year() - 1 : now.year();

    return [
        dayjs(`${schoolYearStart}-09-01`),
        dayjs(`${schoolYearStart + 1}-08-31`),
    ];
};

const SchoolCompare: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(getCurrentAcademicYear());
    const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<number | null>(null);
    const [indicatorFilterCategoryId, setIndicatorFilterCategoryId] = useState<number | 'all'>('all');
    const [selectedIndicators, setSelectedIndicators] = useState<number[]>([]);
    const [selectedEducationIndicators, setSelectedEducationIndicators] = useState<number[]>([]);

    const {
        indicators,
        isFetching: isIndicatorsFetching,
        schoolComparison,
        timeSeriesComparison,
        isFetchingComparison,
        isFetchingTimeSeriesComparison,
        isFetchingExport,
        schoolIndicatorCategories,
        specialIndicators,
    } = useSelector((state: CombinedStates) => state.indicator);

    useEffect(() => {
        if (isEmpty(schoolIndicatorCategories)) {
            dispatch(getSchoolIndicatorCategories({}));
        }
    }, [dispatch, schoolIndicatorCategories]);

    // Load indicators if not available
    useEffect(() => {
        if (isEmpty(indicators)) {
            dispatch(getIndicators({}));
        }
    }, [dispatch, indicators]);

    const handleCategorySelect = (categoryId: number | null) => {
        setSelectedCategories(categoryId);
        // Clear individual indicators when categories are selected
        setSelectedIndicators([]);
    };

    const handleIndicatorFilterCategoryChange = (value: number | 'all' | undefined) => {
        setIndicatorFilterCategoryId(value === undefined || value === null ? 'all' : value);
    };

    const handleIndicatorSelect = (indicatorIds: number[]) => {
        setSelectedIndicators(indicatorIds);
        // Clear categories when individual indicators are selected
        setSelectedCategories(null);
    };

    const handleEducationIndicatorSelect = (indicatorIds: number[]) => {
        setSelectedEducationIndicators(indicatorIds);
    };

    const handleRefreshIndicators = () => {
        dispatch(getIndicators({}));
    };

    const createComparisonPayload = () => {
        if (!dateRange || selectedSchools.length < 2) {
            return null;
        }

        const payload: any = {
            school_ids: selectedSchools.map(id => parseInt(id, 10)),
            date_from: dateRange[0].format('YYYY-MM-DD'),
            date_to: dateRange[1].format('YYYY-MM-DD'),
        };

        // Add either category_id or indicator_ids
        if (selectedCategories !== null) {
            payload.category_id = selectedCategories;
        } else if (selectedIndicators.length > 0) {
            payload.indicator_ids = selectedIndicators;
        }

        // Add education indicators as special_indicators
        if (selectedEducationIndicators.length > 0) {
            payload.special_indicators = selectedEducationIndicators;
        }

        return payload;
    };

    const handleCompare = () => {
        const payload = createComparisonPayload();
        if (!payload) {
            return;
        }

        dispatch(compareSchools(payload));
    };

    const handleCompareTimeSeries = () => {
        const payload = createComparisonPayload();
        if (!payload) {
            return;
        }

        dispatch(compareSchoolsTimeSeries(payload));
    };

    const handleExportExcel = async () => {
        const payload = createComparisonPayload();
        if (!payload) {
            return;
        }

        try {
            const response = await dispatch(exportSchoolsComparison(payload)).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mokyklu_palyginimas_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const handleExportPdf = async () => {
        const payload = createComparisonPayload();
        if (!payload) {
            return;
        }

        try {
            const response = await dispatch(exportSchoolsComparisonPdf(payload)).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/pdf',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mokyklu_palyginimas_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF export failed:', error);
        }
    };

    const canCompare = selectedSchools.length >= 2
        && (selectedCategories !== null || selectedIndicators.length > 0 || selectedEducationIndicators.length > 0);

    // Filter indicators based on assigned_scope (all, types, or schools) and optional category filter
    type CategoryWithPivot = { id?: number; pivot?: { category_id?: number } };
    const filteredIndicators = indicators
        .filter(indicator => ['all', 'types', 'schools'].includes(indicator.assigned_scope))
        .filter(indicator => {
            if (indicatorFilterCategoryId === 'all') return true;
            const indicatorCategoryIds = (indicator.categories as CategoryWithPivot[] | undefined)?.map(
                (cat: CategoryWithPivot) => cat?.pivot?.category_id ?? cat?.id,
            ) ?? [];
            return indicatorCategoryIds.includes(indicatorFilterCategoryId);
        });

    // Filter education indicators (education_center or education_department)
    const filteredEducationIndicators = indicators.filter(indicator => ['education_center', 'education_department'].includes(indicator.assigned_scope));

    return (
        <div className="school-compare">
            <Title level={4}>Mokyklų palyginimas</Title>

            {/* Date Range Selection */}
            <Card title="Laikotarpis" className="selection-card">
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={8}>
                        <Text strong>Analizės laikotarpis:</Text>
                    </Col>
                    <Col xs={24} sm={16}>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setDateRange([dates[0], dates[1]]);
                                }
                            }}
                            format="YYYY-MM-DD"
                            placeholder={['Pradžios data', 'Pabaigos data']}
                            className="date-range-picker"
                            allowClear={false} />
                    </Col>
                </Row>
            </Card>

            <SchoolSelection
                selectedSchools={selectedSchools}
                onChange={setSelectedSchools}
                placeholder="Pasirinkite mokyklas palyginimui" />

            {/* Indicators Selection */}
            <Card title="Rodiklių pasirinkimas" className="selection-card">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text strong>Rodiklių kategorijos:</Text>
                        <Select
                            placeholder="Pasirinkite rodiklių kategoriją"
                            value={selectedCategories}
                            onChange={handleCategorySelect}
                            style={{ width: '100%', marginTop: 8 }}
                            allowClear>
                            {schoolIndicatorCategories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.title}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <Divider>arba</Divider>

                    <div>
                        <Text strong>Individualūs rodikliai:</Text>
                        <Row gutter={12} align="middle" style={{ marginTop: 8 }}>
                            <Col xs={24} sm={10} md={8}>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Filtruoti pagal kategoriją:</Text>
                                <Select
                                    value={indicatorFilterCategoryId}
                                    onChange={handleIndicatorFilterCategoryChange}
                                    style={{ width: '100%' }}
                                    allowClear
                                    placeholder="Visi rodikliai">
                                    <Option value="all">Visi rodikliai</Option>
                                    {schoolIndicatorCategories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.title}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={14} md={16}>
                                {isIndicatorsFetching ? (
                                    <div className="loading-container">
                                        <Spin size="small" />
                                        <Text>Kraunami rodikliai...</Text>
                                    </div>
                                ) : (
                                    <>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Pasirinkite individualius rodiklius:</Text>
                                        <Select
                                            mode="multiple"
                                            placeholder="Pasirinkite individualius rodiklius"
                                            value={selectedIndicators}
                                            onChange={handleIndicatorSelect}
                                            style={{ width: '100%' }}
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) => {
                                                const optionText = String(option?.children || '').toLowerCase();
                                                const inputLower = input.toLowerCase();

                                                // Check if input starts with 'r' or 'R' followed by numbers (ID search)
                                                const idMatch = inputLower.match(/^r?(\d+)$/);
                                                if (idMatch && idMatch[1]) {
                                                    const searchId = idMatch[1];
                                                    return optionText.includes(`r${searchId}`) || optionText.includes(searchId);
                                                }

                                                // Regular text search
                                                return optionText.includes(inputLower);
                                            }}>
                                            {filteredIndicators.map(indicator => (
                                                <Option key={indicator.id} value={indicator.id}>
                                                    R{indicator.id} - {indicator.title}
                                                </Option>
                                            ))}
                                        </Select>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Space>
            </Card>

            {/* Education Indicators Selection */}
            <Card title="Švietimo skyriaus/centro rodiklių pasirinkimas" className="selection-card">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Text strong>Švietimo skyriaus ar centro rodikliai:</Text>
                                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                                    Įtraukti švietimo skyriaus ar centro rodiklius į palyginimą
                                </Text>
                            </div>
                            <Tooltip title="Atnaujinti duomenis">
                                <Button
                                    type="text"
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshIndicators}
                                    loading={isIndicatorsFetching}
                                    style={{
                                        color: '#1890ff',
                                        border: 'none',
                                        boxShadow: 'none',
                                    }} />
                            </Tooltip>
                        </div>

                        {isIndicatorsFetching ? (
                            <div className="loading-container" style={{ marginTop: 16 }}>
                                <Spin size="small" />
                                <Text>Kraunami rodikliai...</Text>
                            </div>
                        ) : (
                            <Select
                                mode="multiple"
                                placeholder="Pasirinkite švietimo skyriaus/centro rodiklius"
                                value={selectedEducationIndicators}
                                onChange={handleEducationIndicatorSelect}
                                style={{ width: '100%', marginTop: 8 }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => {
                                    const optionText = String(option?.children || '').toLowerCase();
                                    const inputLower = input.toLowerCase();

                                    // Check if input starts with 'r' or 'R' followed by numbers (ID search)
                                    const idMatch = inputLower.match(/^r?(\d+)$/);
                                    if (idMatch && idMatch[1]) {
                                        const searchId = idMatch[1];
                                        return optionText.includes(`r${searchId}`) || optionText.includes(searchId);
                                    }

                                    // Regular text search
                                    return optionText.includes(inputLower);
                                }}>
                                {filteredEducationIndicators.map(indicator => (
                                    <Option key={indicator.id} value={indicator.id}>
                                        R{indicator.id} - {indicator.title}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </div>
                </Space>
            </Card>

            {/* Compare Button */}
            <div className="compare-actions">
                <Space>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleCompare}
                        disabled={!canCompare}
                        loading={isFetchingComparison}>
                        Palyginti rodiklių rezultatus
                    </Button>
                    <Button
                        type="default"
                        size="large"
                        onClick={handleCompareTimeSeries}
                        disabled={!canCompare}
                        loading={isFetchingTimeSeriesComparison}
                        icon={<span>📈</span>}>
                        Palyginti rodiklių reikšmes laike
                    </Button>
                    <Button
                        type="default"
                        size="large"
                        loading={isFetchingExport}
                        onClick={handleExportExcel}
                        disabled={!canCompare}
                        icon={<span>📊</span>}>
                        Eksportuoti į Excel
                    </Button>
                    <Button
                        type="default"
                        size="large"
                        loading={isFetchingExport}
                        onClick={handleExportPdf}
                        disabled={!canCompare}
                        icon={<span>📄</span>}>
                        Eksportuoti į PDF
                    </Button>
                </Space>
            </div>

            {/* Comparison Results */}
            {schoolComparison && (
                <SchoolComparisonResults
                    schoolComparison={schoolComparison}
                    specialIndicators={specialIndicators} />
            )}

            {/* Time Series Comparison Results */}
            {timeSeriesComparison && (
                <TimeSeriesComparisonResults timeSeriesComparison={timeSeriesComparison} />
            )}
        </div>
    );
};

export default SchoolCompare;
